// 농심DAS — 상태 한글 이름표 · 오류 코드 문구 · 실패 문구 읽기 · 파일 저장 도우미(목록·작업 화면 공용).
// 상태·오류 코드는 서버 backend/routers/pdas.py 와 같다(P1 §3-3·§3-4 가 정본).
import { kstDateOf, kstToday } from '../../utils/kstTime';

export const JOB_STATUS_LABELS = { draft: '준비', active: '작업 중', closed: '마감' };
export const BUNDLE_STATUS_LABELS = {
  pending: '대기', filling: '채우는 중', complete: '완성', forced: '강제 마감', removed: '빼냄',
};

export const labelOf = (map, code) => map[code] || code || '—';

// 서버 message 가 없을 때만 쓴다(서버 문구가 먼저다).
export const ERROR_TEXTS = {
  no_free_cell: '빈 셀이 없습니다 — 완성된 셀을 먼저 빼 주세요',
  not_in_plan: '명단에 없는 바코드입니다',
  barcode_done: '이 바코드의 묶음은 모두 배정됐습니다(남는 상품)',
  job_not_active: '작업 중이 아닙니다',
  bad_barcode: '바코드를 읽지 못했습니다',
  stale_cell: '화면이 늦었습니다 — 새로 고쳤으니 다시 눌러 주세요',
  not_complete: '아직 채우는 중인 셀입니다',
  empty_cell: '빈 셀입니다',
  not_filling: '채우는 중인 셀만 강제 마감할 수 있습니다',
  cell_occupied: '그 셀에 이미 다른 묶음이 있습니다',
  already_removed: '이미 빼낸 묶음입니다 — 먼저 빼냄을 취소해 주세요',
  nothing_to_undo: '되돌릴 수 있는 넣기가 아닙니다',
  cells_in_use: '아직 빼내지 않은 셀이 있습니다',
  pending_left: '아직 시작 안 한 묶음이 남았습니다',
  plan_empty: '명단이 비어 있습니다 — 먼저 명단을 올려 주세요',
  plan_invalid: '명단에 오류가 있습니다 — 명단을 바꾸지 않았습니다',
  job_not_draft: '준비 중인 작업이 아닙니다',
  job_closed: '마감된 작업입니다',
  bad_input: '입력값이 올바르지 않습니다',
  not_found: '찾을 수 없습니다',
  file_too_large: '명단 파일은 5MB 까지입니다',
  bad_file_type: 'xlsx 또는 csv 파일만 올릴 수 있습니다',
  scan_id_required: '스캔 번호가 없습니다 — 화면을 새로 고쳐 주세요',
};

// 최근 3건 줄의 짧은 거절 사유(없으면 전체 문구).
export const REJECT_SHORT = {
  no_free_cell: '빈 셀 없음',
  not_in_plan: '명단에 없음',
  barcode_done: '남는 상품',
  bad_barcode: '읽기 실패',
  job_not_active: '작업 중 아님',
};

// 오류 본문의 detail 객체({code, message, …}) — 없거나 글자면 null(동기 · JSON 응답용).
export function errorDetail(err) {
  const detail = err?.response?.data?.detail;
  return detail && typeof detail === 'object' && !Array.isArray(detail) ? detail : null;
}

// 응답 자체가 없다(끊김·시간 초과) — 같은 요청을 다시 보내도 되는 경우.
export const isNetworkError = (err) => !!err && !err.response;

// detail(객체·글자)을 문구로 — 객체면 message → 코드 표 → fallback.
function detailText(detail, fallback) {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    if (typeof detail.message === 'string' && detail.message.trim()) return detail.message;
    if (detail.code && ERROR_TEXTS[detail.code]) return ERROR_TEXTS[detail.code];
  }
  return fallback;
}

// 동기 문구(JSON 응답 — 스캔·빼냄처럼 바로 그려야 하는 곳).
export function errorTextSync(err, fallback = '요청을 처리하지 못했습니다.') {
  if (isNetworkError(err)) return '서버에 연결하지 못했습니다.';
  return detailText(err?.response?.data?.detail, fallback);
}

// jsdom 16 에는 Blob.text() 가 없다 → FileReader 갈래로 읽는다(contractLabels 와 같다).
const readBlobText = (blob) => (typeof blob.text === 'function'
  ? blob.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  }));

// 🔴 엑셀 받기(responseType 'blob')는 오류 본문도 Blob 이라 data.detail 이 늘 undefined 다 → 본문을 읽어 detail 을 꺼낸다.
export async function errorText(err, fallback = '요청을 처리하지 못했습니다.') {
  const data = err?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    let detail;
    try {
      const text = await readBlobText(data);
      try { detail = JSON.parse(text)?.detail; } catch { /* 파싱 실패면 대체 문구 */ }
    } catch { /* 읽기 실패면 대체 문구 */ }
    return detailText(detail, fallback);
  }
  return errorTextSync(err, fallback);
}

// 파일 이름에 못 쓰는 글자(\ / : * ? " < > |)를 _ 로.
export const safeFileName = (s) => String(s ?? '').replace(/[\\/:*?"<>|]/g, '_').trim();

// 결과 엑셀 저장 이름 — 마감했으면 마감일(KST), 아니면 오늘(KST).
export function resultFileName(job) {
  const day = (job?.closed_at ? kstDateOf(job.closed_at) : kstToday()) || kstToday();
  return `농심DAS_${safeFileName(job?.title)}_${day.replace(/-/g, '')}.xlsx`;
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* 지울 수 없으면 둔다 */ } }, 4000);
}
